import React, { useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-moment";

// Register all necessary components including Tooltip and Legend
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  Legend
);

// Custom plugin to draw a vertical line on hover
const verticalLinePlugin = {
  id: "verticalLinePlugin",
  afterDraw: (chart) => {
    const activeElements = chart.tooltip?.getActiveElements();
    if (activeElements && activeElements.length) {
      const { ctx } = chart;
      const { chartArea } = chart;

      // Ensure chart area is defined
      if (!chartArea) return;

      const { x } = activeElements[0].element;
      // console.log("Drawing line at x:", x); // Debugging line to check the x position

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#C0C0C0"; // Color of the vertical line
      ctx.stroke();
      ctx.restore();
    }
  },
};

const redBackgroundPlugin = {
  id: "redBackgroundPlugin",
  beforeDatasetsDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;
    // const minLookingDown = chart.config.options.plugins.custom?.minRedColor;
    // const minLookingDown =
    //   Math.floor(chart.config.options.plugins.custom?.minRedColor / 5) * 5;

    const minLookingDown = chart.config.options.plugins.custom?.minRedColor
      ? chart.config.options.plugins.custom?.minValue
      : null;
    const maxLookingDown =
      chart.config.options.plugins.custom?.blueColor.minValue;

    // Shade the "looking down" range
    if (minLookingDown !== null && maxLookingDown !== null) {
      const minY = scales.y.getPixelForValue(minLookingDown);
      const maxY = scales.y.getPixelForValue(maxLookingDown);

      ctx.save();
      ctx.fillStyle = "rgba(255, 0, 0, 0.3)";
      ctx.fillRect(
        chartArea.left,
        maxY,
        chartArea.right - chartArea.left,
        minY - maxY
      );
      ctx.restore();
    }

    // Shade the "looking left" range
  },
};
const blueBackgroundPlugin = {
  id: "blueBackgroundPlugin",
  beforeDatasetsDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;

    const minColor = chart.config.options.plugins.custom?.maxRedColor
      ? chart.config.options.plugins.custom?.blueColor.minValue
      : chart.config.options.plugins.custom?.minValue;

    const maxColor = chart.config.options.plugins.custom?.orangeColor?.minValue
      ? chart.config.options.plugins.custom?.blueColor?.maxValue
      : chart.config.options.plugins.custom?.maxValue === 0
      ? chart.config.options.plugins.custom?.maxValue + 2
      : chart.config.options.plugins.custom?.maxValue;

    const minLookingDown = minColor;
    const maxLookingDown = maxColor;

    // const minLookingDown =
    //   chart.config.options.plugins.custom?.oldMinValue === 0
    //     ? -2
    //     : chart.config.options.plugins.custom?.blueColor.minValue;

    // Shade the "looking down" range
    if (minLookingDown !== null && maxLookingDown !== null) {
      const minY = scales.y.getPixelForValue(minLookingDown);
      const maxY = scales.y.getPixelForValue(maxLookingDown);

      ctx.save();
      ctx.fillStyle = "rgba(135, 206, 250, 0.4)";
      ctx.fillRect(
        chartArea.left,
        maxY,
        chartArea.right - chartArea.left,
        minY - maxY
      );
      ctx.restore();
    }

    // Shade the "looking left" range
  },
};
const orangeBackgroundPlugin = {
  id: "orangeBackgroundPlugin",
  beforeDatasetsDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;

    const min = chart.config.options.plugins.custom?.blueColor?.maxValue;
    const max = chart.config.options.plugins.custom?.orangeColor?.maxValue
      ? chart.config.options.plugins.custom?.maxValue
      : null;

    // const max = chart.config.options.plugins.custom?.orangeColor.maxValue;
    // const max =
    //   Math.ceil(chart.config.options.plugins.custom?.orangeColor.maxValue / 5) *
    //   5;

    // Shade the "looking down" range
    if (min !== null && max !== null) {
      const minY = scales.y.getPixelForValue(min);
      const maxY = scales.y.getPixelForValue(max);

      ctx.save();
      ctx.fillStyle = "rgba(255, 200, 150, 0.6)";
      ctx.fillRect(
        chartArea.left,
        maxY,
        chartArea.right - chartArea.left,
        minY - maxY
      );
      ctx.restore();
    }

    // Shade the "looking left" range
  },
};

function InterviewChart({
  dataPoints,
  updateVideoTrack,
  chartTitle,
  minRedColor,
  maxRedColor,
  blueColor,
  orangeColor,
  chartLeftTitleNegative,
  chartLeftTitlePositive,
}) {
  const timeLabels = dataPoints?.map((point) => new Date(point.time));
  const values = dataPoints?.map((point) => ({
    x: point.time,
    y: point.value,
    label: point.label,
  }));
  const labelText = dataPoints?.map((point) => point.label);

  const maxTime = dataPoints ? dataPoints[dataPoints?.length - 1]?.time : 0;
  let minValue = 0;
  let oldMinValue = 0;
  let maxValue = 0;
  const yValues = values?.map((point) => point.y);
  if (yValues) {
    oldMinValue = Math.min(...yValues);
    maxValue = Math.max(...yValues);

    if (oldMinValue === 0) {
      minValue = Math.floor(oldMinValue / 5) * 5 - 2;
    } else {
      minValue = Math.floor(oldMinValue / 5) * 5;
    }

    maxValue = Math.ceil(maxValue / 5) * 5;
  }

  const convertSecondsToTimeX = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${
        remainingSeconds < 10 ? "0" : ""
      }${remainingSeconds}hr`;
    } else if (minutes > 0) {
      return `${minutes}:${
        remainingSeconds < 10 ? "0" : ""
      }${remainingSeconds}m`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const data = {
    labels: timeLabels,
    datasets: [
      {
        label: `${chartTitle}`,
        data: values,
        textLabel: labelText,
        borderColor: "#4BC0C0",
        borderWidth: 1.5,
        fill: true,
        pointRadius: 0, // Removes the dots
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;
          if (!chartArea) return null;

          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.bottom,
            0,
            chartArea.top
          );
          gradient.addColorStop(0, "#FFC1C1");
          gradient.addColorStop(0.5, "#C1F0C1");
          gradient.addColorStop(1, "#C1D1FF");
          return gradient;
        },
        segment: {
          borderColor: (ctx) => {
            const dataIndex = ctx.p1DataIndex; // Index of the current segment's end point
            const dataset = ctx.chart.data.datasets[0].data;
            const currentPoint = dataset[dataIndex];

            // Check if the current, previous, or next data point has a label indicating "No face detected"
            if (
              currentPoint?.label === "No face detected" ||
              currentPoint?.label === "Multiple Faces Detected"
            ) {
              return "rgba(255, 0, 0, 0.6)";
            }

            return "#4BC0C0";
          },
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    type: "line",
    scales: {
      x: {
        min: 0,
        max: maxTime,
        type: "linear",
        position: "bottom",
        ticks: {
          callback: (value) => convertSecondsToTimeX(value),
          font: {
            weight: 700,
          },
        },
        title: {
          display: true,
          text: "Time (minutes)",
          font: {
            weight: 700,
          },
        },
      },
      y: {
        type: "linear",
        min: minValue,
        max: maxValue === 0 ? maxValue + 2 : maxValue,
        ticks: {
          stepSize: 5, // Set your desired step size here
          font: {
            weight: 600, // Make the y-axis font bold
          },
        },
        title: {
          display: true,
          text: [
            "Head Orientation",
            `Looking ${chartLeftTitleNegative} (-ve) Looking ${chartLeftTitlePositive} (+ve)`,
          ],
          font: {
            weight: "bold", // Set title text to bold
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "white",
        displayColors: false,
        titleColor: "black", // Title text color
        bodyColor: "black", // Body text color
        titleFont: {
          size: "15px", // Font size for the title
          weight: 600, // Font weight for the title
          family: "Inter, sans-serif",
        },
        bodyFont: {
          size: "14px",
          family: "Inter, sans-serif",
        },
        padding: 12,
        callbacks: {
          title: (context) => {
            const label = context[0]?.raw;
            return `${convertSecondsToTimeX(label.x)}`;
          },
          label: (context) => {
            const displayText = context?.raw;
            const dataPoint = context.parsed.y;
            return [`${displayText?.label || "N/A"}: ${dataPoint.toFixed(1)}`];
          },
        },
      },
      custom: {
        minRedColor,
        maxRedColor,
        blueColor,
        orangeColor,
        oldMinValue,
        minValue,
        maxValue,
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const clickedElement = elements[0];
        const xValue = clickedElement.element.$context.parsed.x;
        const time = clickedElement.element.$context.parsed.x;
        updateVideoTrack(xValue);
        window.scrollTo({
          top: 200,
          behavior: "smooth",
        });
      }
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      point: {
        radius: 5,
        hoverRadius: 7,
      },
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <div className="h-[280px]">
      <Line
        data={data}
        options={options}
        height={50}
        plugins={[
          verticalLinePlugin,
          redBackgroundPlugin,
          blueBackgroundPlugin,
          orangeBackgroundPlugin,
        ]} // Add custom plugin here
      />
    </div>
  );
}

export default InterviewChart;
